import { useModularPermissionsStore } from "@/features/mod-perms/stores/modular-permissions.store";
import {
  InputSwitchStateEnum,
  PlatformEnum,
} from "@/types/modular-permissions.types";
import { type HexString } from "@/types/general.types";
import { getPredeterminedAddress } from "@/utils/get-predetermined-address.util";
import { ethers } from "ethers";
import React, { useState } from "react";
import { useAccount } from "wagmi";

const useDelegateCreation = () => {
  const { addDelegation, addToPendingCreations, isAlreadyDelegated } =
    useModularPermissionsStore();
  const [walletAddress, setWalletAddress] = useState<string>("");
  const [walletAddressError, setWalletAddressError] = useState<string | null>(
    null,
  );
  const [isLoadingSocialAccount, setIsLoadingSocialAccount] = useState(false);

  const [socialPlatform, setSocialPlatform] = useState<
    PlatformEnum | undefined
  >(PlatformEnum.Telegram);

  const [inputState, setInputState] = useState<InputSwitchStateEnum>(
    InputSwitchStateEnum.Wallet,
  );
  const [userHandle, setUserHandle] = useState<string>("");

  const account = useAccount();

  async function createDelegate(): Promise<void> {
    setWalletAddressError(null);

    const isValidAddress = ethers.isAddress(
      (walletAddress! as HexString).toLowerCase(),
    );

    //? Check if the input state is platform
    if (inputState === InputSwitchStateEnum.Platform) {
      if (!socialPlatform)
        return setWalletAddressError("Please select a platform");

      if (!userHandle) return setWalletAddressError("Please enter a user id");

      try {
        const data = await getPredeterminedAddress(
          socialPlatform,
          account.chainId,
          userHandle,
        );

        if (data) {
          if (isAlreadyDelegated(data.account)) {
            setWalletAddressError("This account has already been delegated");
            return;
          }
          if (data.account) {
            addDelegation({
              vault: account.address!,
              walletAddress: data.account,
              isSocialAccount: true,
              platform: socialPlatform,
              userId: data.hashedId,
            });
            addToPendingCreations(data.account);
          }
        }
      } catch (error) {
        console.log(error);
        setWalletAddressError("An error occurred while creating the delegate.");
      }
    }

    //? Check if the input state is wallet
    if (inputState === InputSwitchStateEnum.Wallet) {
      if (!isValidAddress)
        return setWalletAddressError("Please enter a valid address");
      const isAlreadyExisting = isAlreadyDelegated(walletAddress as HexString);
      if (isAlreadyExisting)
        return setWalletAddressError("This address has already been delegated");
      addDelegation({
        vault: account.address!,
        walletAddress: walletAddress as HexString,
      });
      addToPendingCreations(walletAddress as HexString);
    }

    //? Reset wallet address field
    setWalletAddress("");
    setUserHandle("");
  }

  function handleInputStateToggle(state: InputSwitchStateEnum) {
    reset();
    setInputState(state);
  }

  function handleWalletInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setWalletAddressError(null);
    setWalletAddress(e.target.value);
  }

  function reset() {
    setWalletAddress("");
    setUserHandle("");
    setWalletAddressError(null);
    setSocialPlatform(undefined);
  }

  return {
    walletAddress,
    walletAddressError,
    socialPlatform,
    userHandle,
    inputState,
    isLoadingSocialAccount,
    setInputState,
    setUserHandle,
    setSocialPlatform,
    createDelegate,
    handleWalletInputChange,
    setIsLoadingSocialAccount,
    handleInputStateToggle,
    reset,
  };
};

export default useDelegateCreation;
